import { getCurrentEasternDate } from "@nlc/shared-types";
import dayjs from "dayjs";

import type { BadgeVariant } from "@/components/badge";

import { convertUTCtoEastern } from "./timezoneConverter";

export type DueDateDisplay = {
  badgeVariant: BadgeVariant;
  colorClass: string;
  isPill: boolean;
  label: string;
};

const DAY_NAME_RANGE_END = 4;

function buildDisplay(
  partial: Partial<DueDateDisplay> & Pick<DueDateDisplay, "label">,
): DueDateDisplay {
  return {
    badgeVariant: "primary",
    colorClass: "text-text-tertiary",
    isPill: false,
    ...partial,
  };
}

export function getDueDateDisplay(
  utcDateString: null | string | undefined,
): DueDateDisplay {
  if (!utcDateString) {
    return buildDisplay({ label: "--" });
  }

  if (utcDateString === "in_queue" || utcDateString === "In queue") {
    return buildDisplay({ label: "In queue" });
  }

  const easternDateString = convertUTCtoEastern(utcDateString, "YYYY-MM-DD");

  if (!easternDateString) {
    return buildDisplay({ label: "--" });
  }

  const todayEastern = getCurrentEasternDate();
  const diffInDays = dayjs(easternDateString).diff(dayjs(todayEastern), "day");

  if (diffInDays === 0) {
    return buildDisplay({
      badgeVariant: "success",
      isPill: true,
      label: "Today",
    });
  }

  if (diffInDays === 1) {
    return buildDisplay({
      badgeVariant: "blue",
      isPill: true,
      label: "Tomorrow",
    });
  }

  if (diffInDays === -1) {
    return buildDisplay({
      badgeVariant: "error",
      isPill: true,
      label: "Yesterday",
    });
  }

  if (diffInDays > 1 && diffInDays <= DAY_NAME_RANGE_END) {
    return buildDisplay({
      colorClass: "text-utility-blue-700",
      label: dayjs(easternDateString).format("dddd"),
    });
  }

  if (diffInDays > DAY_NAME_RANGE_END) {
    return buildDisplay({
      colorClass: "text-utility-blue-700",
      label: convertUTCtoEastern(utcDateString),
    });
  }

  return buildDisplay({
    colorClass: "text-text-error-primary",
    label: convertUTCtoEastern(utcDateString),
  });
}
